/**
 * The fauna half of the world layer, split out of `worldview.ts` in stage 7a
 * because graduating the bestiary gave creatures things structures never had:
 * five states, a hit flash, and a body that outlives the sim entity.
 *
 * The sim already knew all of it. `attackTargetId` says a fish has its teeth in
 * something, the `enemyHit` event says one just took damage, and an id leaving
 * `state.enemies` says one stopped existing. None of it reached the screen
 * before: a fish that was eating your smelter looked exactly like a fish
 * swimming past it, and a kill was a sprite that simply was not there next
 * frame.
 *
 * Nothing here touches the simulation. Every state is read, never set.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import { BESTIARY_IDS, creatureById } from '../art/bestiary.ts';
import type { CreatureState } from '../art/creature.ts';
import { buildCreature } from '../art/fauna.ts';
import { TH, TW } from '../render/iso.ts';
import type { DepthPalette } from '../render/palette.ts';
import { TextureCache } from '../render/pipeline.ts';
import { PixelSurface } from '../render/surface.ts';
import type { Sim } from '../sim/sim.ts';
import { SPECIES } from '../sim/species.ts';
import type { Enemy } from '../sim/types.ts';

/**
 * Ticks a hit flash lasts. Doc 10 writes "one frame", which at 30 Hz is 33 ms —
 * long enough to see on a boss and easy to miss on a krill in a crowd, so it
 * gets two. It is still short enough that a shoal under sustained fire reads as
 * a shoal under fire rather than a strobe, and the flash setting can switch it
 * off entirely (doc 18 §3).
 */
const HURT_TICKS = 2;

/** Ticks a body takes to come apart: four dissolve steps, three ticks each. */
const DEATH_TICKS = 12;

/** The four phase steps every creature animation is quantized to (Law L4). */
const PHASE_STEPS = 4;

interface EnemyView {
  sprite: Sprite;
  dir: 1 | -1;
  species: string;
}

/** A body still on screen after its enemy left the simulation. */
interface Dying {
  sprite: Sprite;
  species: string;
  dir: 1 | -1;
  x: number;
  y: number;
  until: number;
}

export class FaunaView {
  private readonly views = new Map<number, EnemyView>();
  readonly prevPos = new Map<number, { x: number; y: number }>();
  private readonly riledWaves = new Set<number>();
  /** Enemies flashing a hit: id → the tick the flash ends. */
  private readonly hurt = new Map<number, number>();
  private readonly dying: Dying[] = [];
  private readonly anchors = new Map<string, { ax: number; ay: number }>();

  private readonly sim: () => Sim;
  private readonly layer: Container;
  private readonly cache: TextureCache;
  private readonly depth: DepthPalette;
  /** Doc 18 §3's flash intensity, 0..1. At 0 nothing flashes, including this. */
  private readonly flash: () => number;

  constructor(
    sim: () => Sim,
    layer: Container,
    cache: TextureCache,
    depth: DepthPalette,
    flash: () => number,
  ) {
    this.sim = sim;
    this.layer = layer;
    this.cache = cache;
    this.depth = depth;
    this.flash = flash;
  }

  markRiled(waveId: number): void {
    this.riledWaves.add(waveId);
  }

  /** From the `enemyHit` event. The whole body goes to ink for two ticks. */
  noteHit(enemyId: number): void {
    if (this.flash() <= 0) return;
    this.hurt.set(enemyId, this.sim().state.tick + HURT_TICKS);
  }

  capturePrev(): void {
    for (const e of this.sim().state.enemies) this.prevPos.set(e.id, { x: e.x, y: e.y });
  }

  clear(): void {
    for (const v of this.views.values()) v.sprite.destroy();
    for (const d of this.dying) d.sprite.destroy();
    this.views.clear();
    this.dying.length = 0;
    this.prevPos.clear();
    this.riledWaves.clear();
    this.hurt.clear();
    this.anchors.clear();
  }

  sync(alpha: number): void {
    const sim = this.sim();
    const tick = sim.state.tick;
    const seen = new Set<number>();

    for (const e of sim.state.enemies) {
      seen.add(e.id);
      let v = this.views.get(e.id);
      if (!v) {
        v = { sprite: new Sprite(), dir: -1, species: e.species };
        this.layer.addChild(v.sprite);
        this.views.set(e.id, v);
      }
      const p = this.prevPos.get(e.id) ?? { x: e.x, y: e.y };
      const x = p.x + (e.x - p.x) * alpha;
      const y = p.y + (e.y - p.y) * alpha;
      const sdx = e.x - p.x - (e.y - p.y);
      if (sdx > 0.001) v.dir = 1;
      else if (sdx < -0.001) v.dir = -1;

      const state = this.stateOf(e, p, tick);
      const phase = ((tick >> 2) + e.id) % PHASE_STEPS;
      const riled = this.riledWaves.has(e.waveId);
      // submerged is not "faint", it is GONE — the breach dive removes the fish
      // from the water for two seconds and the map should say so (doc 06)
      v.sprite.visible = e.divingUntil <= tick;
      v.sprite.texture = this.texture(e.species, state, phase, v.dir, riled, !sim.visible(e));
      this.place(v.sprite, e.species, v.dir, x, y);
      v.sprite.zIndex = x + y + 0.5;
    }

    for (const [id, v] of this.views) {
      if (seen.has(id)) continue;
      this.bury(v, id, tick);
      this.views.delete(id);
      this.prevPos.delete(id);
      this.hurt.delete(id);
    }
    this.reapDying(tick);
  }

  /**
   * A creature that left the simulation keeps its sprite for twelve ticks and
   * comes apart. The position is the last one the view held, so a body does not
   * teleport to a tile centre on the frame it dies.
   */
  private bury(v: EnemyView, id: number, tick: number): void {
    if (!BESTIARY_IDS.has(v.species) || !creatureById(v.species).states.includes('death')) {
      v.sprite.destroy();
      return;
    }
    const p = this.prevPos.get(id);
    if (!p) {
      v.sprite.destroy();
      return;
    }
    this.dying.push({ sprite: v.sprite, species: v.species, dir: v.dir, x: p.x, y: p.y, until: tick + DEATH_TICKS });
  }

  private reapDying(tick: number): void {
    for (let k = this.dying.length - 1; k >= 0; k--) {
      const d = this.dying[k]!;
      if (tick >= d.until) {
        d.sprite.destroy();
        this.dying.splice(k, 1);
        continue;
      }
      const gone = (DEATH_TICKS - (d.until - tick)) / DEATH_TICKS;
      const step = Math.min(PHASE_STEPS - 1, Math.floor(gone * PHASE_STEPS));
      d.sprite.texture = this.texture(d.species, 'death', step, d.dir, false, false);
      this.place(d.sprite, d.species, d.dir, d.x, d.y);
    }
  }

  /**
   * The state the sim is already describing. `hurt` outranks everything because
   * it is the one the player asked for by shooting; `attack` outranks movement
   * because a fish with its teeth in a smelter is the more urgent fact.
   */
  private stateOf(e: Enemy, prev: { x: number; y: number }, tick: number): CreatureState {
    const spec = BESTIARY_IDS.has(e.species) ? creatureById(e.species) : null;
    const has = (s: CreatureState): boolean => spec === null || spec.states.includes(s);
    if ((this.hurt.get(e.id) ?? -1) > tick && has('hurt')) return 'hurt';
    if (e.attackTargetId >= 0 && has('attack')) return 'attack';
    const moved = Math.abs(e.x - prev.x) + Math.abs(e.y - prev.y) > 0.0005;
    return moved && has('move') ? 'move' : 'idle';
  }

  private place(sprite: Sprite, species: string, dir: 1 | -1, x: number, y: number): void {
    const a = this.anchorOf(species, dir);
    const boss = SPECIES[species]?.boss;
    sprite.x = Math.round((x - y) * TW) - a.ax;
    sprite.y = Math.round((x + y) * TH + TH) - a.ay - (boss ? 6 : 4);
  }

  private anchorOf(species: string, dir: 1 | -1): { ax: number; ay: number } {
    const key = `${species}:${dir}`;
    let a = this.anchors.get(key);
    if (!a) {
      const r = this.build(species, 'idle', 0, false, false);
      // Mirroring reflects the canvas, so the anchor lands the same distance
      // from the other edge. Getting this wrong slides every westbound fish one
      // pixel off its own position.
      a = dir === 1 ? { ax: r.ax, ay: r.ay } : { ax: r.surface.width - 1 - r.ax, ay: r.ay };
      this.anchors.set(key, a);
    }
    return a;
  }

  private texture(
    species: string,
    state: CreatureState,
    phase: number,
    dir: 1 | -1,
    riled: boolean,
    hidden: boolean,
  ): Texture {
    const key = `cr:${species}:${state}:${phase}:${dir}:${riled}:${hidden}`;
    return this.cache.get(key, () => {
      const r = this.build(species, state, phase, riled, hidden);
      return dir === -1 ? mirrored(r.surface) : r.surface;
    });
  }

  private build(
    species: string,
    state: CreatureState,
    phase: number,
    riled: boolean,
    hidden: boolean,
  ): { surface: PixelSurface; ax: number; ay: number } {
    const spec = creatureById(species);
    return buildCreature(spec, {
      depth: this.depth,
      state,
      phase: phase / PHASE_STEPS,
      riled,
      hidden,
    });
  }
}

/** Flip a sprite east-to-west. Every creature is authored swimming east. */
function mirrored(src: PixelSurface): PixelSurface {
  const out = new PixelSurface(src.width, src.height);
  for (let y = 0; y < out.height; y++) {
    for (let x = 0; x < out.width; x++) {
      const col = src.get(src.width - 1 - x, y);
      if (col !== 0) out.px(x, y, col, 'set');
    }
  }
  return out.resetOps();
}
